import { getEasternDate } from '~/utils';

export const DateFormatters = {
  /**
   * Format date to full string (e.g., "Wednesday, January 15, 2025")
   * @param {Date} date
   * @returns {string}
   */
  toFullString(date) {
    const options = {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    };
    return new Intl.DateTimeFormat('en-US', options).format(date);
  },

  /**
   * Format date to full date string (e.g., "January 15, 2025")
   * @param {Date} date
   * @returns {string}
   */
  toFullDate(date) {
    const month = date.toLocaleString('en-US', { month: 'long' });
    const day = date.getDate();
    const year = date.getFullYear();

    return `${month} ${day}, ${year}`;
  },

  /**
   * Format date to month/day format (e.g., "01/15")
   * @param {Date} date
   * @returns {string}
   */
  toMonthDate(date) {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    return `${month}/${day}`;
  },

  /**
   * Get weekday name with week indicator (e.g., "This Wednesday", "Next Friday")
   * @param {Date} date - the date to format
   * @param {Date} referenceDate - not used, kept for API compatibility
   * @returns {string}
   */
  toWeekDay(date, referenceDate) {
    const optionsWeekday = { weekday: 'long' };
    const formatterWeekday = new Intl.DateTimeFormat('en-US', optionsWeekday);
    const weekday = formatterWeekday.format(date);
    const weekIndicator = this.getWeekIndicator(date);
    return `${weekIndicator} ${weekday}`;
  },

  /**
   * Get week indicator relative to current Eastern date
   * @param {Date} date - the date to check
   * @returns {string} "This", "Next", or ""
   */
  getWeekIndicator(date) {
    // Get the current date in Eastern Time (like original implementation)
    const { date: easternDate } = getEasternDate();

    // Get the start of the current week (Sunday) in Eastern Time
    const startOfCurrentWeek = new Date(easternDate);
    startOfCurrentWeek.setDate(easternDate.getDate() - easternDate.getDay()); // Set to Sunday

    // Get the start of the next week (next Sunday) in Eastern Time
    const startOfNextWeek = new Date(startOfCurrentWeek);
    startOfNextWeek.setDate(startOfNextWeek.getDate() + 7);

    // Check if the delivery date falls within this week or next week
    if (date >= startOfCurrentWeek && date < startOfNextWeek) {
      return 'This';
    } else if (date >= startOfNextWeek && date < new Date(startOfNextWeek.getTime() + 7 * 24 * 60 * 60 * 1000)) {
      return 'Next';
    } else {
      return ''; // Default case if it's beyond next week
    }
  },

  /**
   * Format date according to specified format
   * @param {Object} options
   * @param {Date} options.date
   * @param {string} options.format - "full-date", "week-day", "month-date"
   * @param {Date} options.referenceDate - reference date for week calculations (optional)
   * @param {number} options.difference - difference between the immediate date and the non-premium date (optional)
   * @returns {string}
   */
  formatForDisplay(options) {
    const { date, format, referenceDate } = options;

    switch (format) {
      case 'full-date':
        return this.toFullDate(date);
      case 'week-day':
        return this.toWeekDay(date, referenceDate);
      case 'month-date':
        return this.toMonthDate(date);
      case 'week-day__month-date':
        return this.toWeekDay(date, referenceDate) + ' ' + '(' + this.toMonthDate(date) + ')';
      default:
        return this.toFullDate(date);
    }
  },
};